import { v4 as uuidv4 } from 'uuid';

import { Node } from './types';

const formatValue = (value: any): string => {
  if (!value) return '';
  if (Array.isArray(value))
    return value.map(formatValue).filter(Boolean).join(', ');
  if (typeof value === 'string' || typeof value === 'number')
    return String(value);
  return 'object';
};

const printValues = (values: any[] = [], clausesValues: any[] = []): string => {
  const formattedValues = values.map(formatValue).filter(Boolean).join(' ');
  const formattedClauses = clausesValues
    .map(formatValue)
    .filter(Boolean)
    .join(' ');
  if (formattedClauses)
    return formattedValues
      ? `${formattedValues} (${formattedClauses})`
      : `(${formattedClauses})`;
  return formattedValues;
};

const filterDeclineDecision = (decisionNode: any) => {
  const decision = decisionNode.outcome['0'];
  return decision === 'decline';
};

const filterRuwDecision = (
  decisionNode: any,
  isPreferredPlusVisible: boolean,
) => {
  const decision = decisionNode.outcome['0'];
  const decisionLabel = decisionNode.outcome['1'];
  const isPreferredPlus = decisionLabel === 'preferred_plus';

  return (
    decision === 'ruw' && (isPreferredPlus ? isPreferredPlusVisible : true)
  );
};

const filterApproveDecision = (
  decisionNode: any,
  isPreferredPlusVisible: boolean,
) => {
  const decision = decisionNode.outcome['0'];
  const decisionLabel = decisionNode.outcome['1'];
  const isPreferredPlus = decisionLabel === 'preferred_plus';

  return (
    decision === 'approve' &&
    !!decisionLabel &&
    (isPreferredPlus ? isPreferredPlusVisible : true)
  );
};

const getConditionNodes = (
  condition: any,
  rulesLookup: any,
  decisionsLookup: any,
): Node => ({
  id: uuidv4(),
  title: condition.src,
  variant: 'default',
  subtitle: printValues(
    [
      condition.fact_name,
      condition.attribute_name,
      condition.op,
      condition.value,
    ],
    [condition.matched_value],
  ),
  children: getRulesNodes(
    decisionsLookup[condition.src]?.rules ?? [],
    rulesLookup,
    decisionsLookup,
  ),
});

const getRuleNodes = (
  ruleId: string,
  rulesLookup: any,
  decisionsLookup: any,
): Node[] => {
  const rule = rulesLookup[ruleId];
  if (!rule) return [];
  return rule.conditions.map((condition: any) =>
    getConditionNodes(condition, rulesLookup, decisionsLookup),
  );
};

const getRulesNodes = (
  rulesIds: string[],
  rulesLookup: any,
  decisionsLookup: any,
): Node[] => {
  return rulesIds
    .map(ruleId => getRuleNodes(ruleId, rulesLookup, decisionsLookup))
    .flat();
};

const mergeNodesWithSameTitle = (nodes: Node[]): Node[] => {
  const nodeMap = new Map<string, Node>();

  for (const node of nodes) {
    const currentSubtitle = Array.isArray(node.subtitle)
      ? node.subtitle
      : node.subtitle
        ? [node.subtitle]
        : [];
    const currentChildren = node.children ?? [];

    if (nodeMap.has(node.title)) {
      const existing = nodeMap.get(node.title)!;
      const existingSubtitle = Array.isArray(existing.subtitle)
        ? existing.subtitle
        : existing.subtitle
          ? [existing.subtitle]
          : [];
      const mergedSubtitle = Array.from(
        new Set([...existingSubtitle, ...currentSubtitle]),
      );
      const mergedChildren = mergeNodesWithSameTitle([
        ...(existing.children ?? []),
        ...currentChildren,
      ]);
      nodeMap.set(node.title, {
        ...existing,
        subtitle: mergedSubtitle,
        children: mergedChildren,
      });
    } else {
      nodeMap.set(node.title, {
        ...node,
        subtitle: Array.from(new Set(currentSubtitle)),
        children: mergeNodesWithSameTitle(currentChildren),
      });
    }
  }

  return Array.from(nodeMap.values());
};

export const getNode = (data: any, isPreferredPlusVisible = false): Node => {
  const decisions = data.snapshot.flatMap(
    (snapshot: any) => snapshot.decisionInformation?.traceMap?.stage_info ?? [],
  );
  const rules = data.snapshot.flatMap(
    (snapshot: any) => snapshot.decisionInformation?.traceMap?.trace_map ?? [],
  );
  const policyId =
    data.snapshot.find((snapshot: any) => !!snapshot?.policyId)?.policyId ??
    'unknown';

  const rulesLookup = Object.fromEntries(
    rules.map((rule: any) => [rule.rule_id, rule]),
  );
  const decisionsLookup = decisions.reduce(
    (acc: any, obj: any) => ({ ...acc, ...obj }),
    {},
  );

  const declinedDecisions = Object.fromEntries(
    Object.entries(decisionsLookup).filter(([_, value]) =>
      filterDeclineDecision(value),
    ),
  ) as any;

  const approvedDecisions = Object.fromEntries(
    Object.entries(decisionsLookup).filter(([_, value]) =>
      filterApproveDecision(value, isPreferredPlusVisible),
    ),
  ) as any;

  const ruwDecisions = Object.fromEntries(
    Object.entries(decisionsLookup).filter(([_, value]) =>
      filterRuwDecision(value, isPreferredPlusVisible),
    ),
  ) as any;

  const children: Node[] = [];

  if (Object.keys(declinedDecisions).length > 0) {
    Object.entries(declinedDecisions).forEach(([key, decision]: any) => {
      children.push({
        id: uuidv4(),
        title: 'Decline',
        subtitle: key,
        variant: 'error',
        children: mergeNodesWithSameTitle(
          getRulesNodes(decision.rules, rulesLookup, decisionsLookup),
        ),
      });
    });
  }

  if (Object.keys(approvedDecisions).length > 0) {
    Object.entries(approvedDecisions).forEach(([key, decision]: any) => {
      children.push({
        id: uuidv4(),
        title: getApproveTitle(decision),
        subtitle: key,
        variant: 'success',
        children: mergeNodesWithSameTitle(
          getRulesNodes(decision.rules, rulesLookup, decisionsLookup),
        ),
      });
    });
  }

  if (Object.keys(ruwDecisions).length > 0) {
    Object.entries(ruwDecisions).forEach(([key, decision]: any) => {
      children.push({
        id: uuidv4(),
        title: 'Ruw',
        subtitle: key,
        variant: 'warning',
        children: mergeNodesWithSameTitle(
          getRulesNodes(decision.rules, rulesLookup, decisionsLookup),
        ),
      });
    });
  }

  return {
    id: uuidv4(),
    title: `Policy ID ${policyId}`,
    variant: 'default',
    children,
  };
};

const getApproveTitle = (node: any) => {
  const title = node.outcome['1'];
  const subtitle = node.outcome['4'];
  if (!subtitle) return title;
  return `${title} ${subtitle}`;
};

export const isSubtitleDefined = (subtitle?: string | string[]) => {
  if (typeof subtitle === 'string') return subtitle.trim().length > 0;
  if (Array.isArray(subtitle)) return subtitle.length > 0;
  return !!subtitle;
};

export const formatSubtitle = (subtitle: string) => {
  return subtitle
    .replace(/map\[(.*?)]/g, (_, content) =>
      content
        .split(/\s+/)
        .map((pair: string) => pair.replace(/:true$/, ''))
        .join(' '),
    )
    .trim();
};

export const isMatchQuery = (
  query: string,
  title: string,
  subtitle?: string | string[],
) => {
  if (!query.trim()) return false;
  const lowerQuery = query.toLowerCase();
  const titleMatches = title.toLowerCase().includes(lowerQuery);

  const subtitleMatches = Array.isArray(subtitle)
    ? subtitle.some(sub => sub.toLowerCase().includes(lowerQuery))
    : typeof subtitle === 'string' &&
      subtitle.toLowerCase().includes(lowerQuery);

  return titleMatches || subtitleMatches;
};

export const convertToD3TreeFormat = (node: Node): any => ({
  name: node.title,
  attributes: {
    subtitle: Array.isArray(node.subtitle)
      ? node.subtitle.join(', ')
      : (node.subtitle ?? ''),
    variant: node.variant,
  },
  children: node.children?.map(convertToD3TreeFormat),
});

export const getNodeSafe = (
  data: any,
  isPreferredPlusVisible = false,
): { node: Node | null; error: string | null } => {
  const errorMsg = 'Rule Tracemap Graph for this policy is not supported';
  try {
    const node = getNode(data, isPreferredPlusVisible);
    if (!node) return { node: null, error: errorMsg };
    return { node, error: null };
  } catch (error) {
    return { node: null, error: errorMsg };
  }
};
